import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import {
    getAuth,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signOut,
    onAuthStateChanged,
} from 'firebase/auth';
import firebaseConfig from '../components/Login/firebase.config';

initializeApp(firebaseConfig);
const auth = getAuth();
const googleProvider = new GoogleAuthProvider();

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const GUEST_KEY = 'pixiy-guest';

// Popups are unreliable on mobile browsers and in-app webviews, where they get
// blocked or throw. Use the redirect flow there and popup on desktop.
const isMobile = () =>
    typeof navigator !== 'undefined' &&
    /Android|iPhone|iPad|iPod|Mobile|Silk|Kindle|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isGuest, setIsGuest] = useState(() => localStorage.getItem(GUEST_KEY) === 'true');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Complete a redirect sign-in if we're returning from Google. Errors here
        // (e.g. auth/unauthorized-domain) surface on the next popup attempt too, so
        // just log them — onAuthStateChanged drives the UI once the user resolves.
        getRedirectResult(auth)
            .then((result) => {
                if (result?.user) {
                    setIsGuest(false);
                    localStorage.removeItem(GUEST_KEY);
                }
            })
            .catch((err) => console.error('Google redirect sign-in failed:', err));

        const unsubscribe = onAuthStateChanged(auth, (u) => {
            setUser(u);
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const signInWithGoogle = async () => {
        // Prefer popup everywhere: it keeps the flow on Google's domain and hands
        // the result straight back. signInWithRedirect breaks when the app domain
        // (vercel.app) differs from the authDomain (firebaseapp.com), because mobile
        // browsers block the cross-site storage the redirect handshake relies on, so
        // the user lands back on the login page still signed out. Fall back to
        // redirect only when the popup is genuinely unavailable (blocked / webview).
        try {
            const result = await signInWithPopup(auth, googleProvider);
            setIsGuest(false);
            localStorage.removeItem(GUEST_KEY);
            return result.user;
        } catch (err) {
            const popupUnavailable =
                err?.code === 'auth/popup-blocked' ||
                err?.code === 'auth/operation-not-supported-in-this-environment' ||
                (isMobile() && err?.code === 'auth/popup-closed-by-user');
            if (popupUnavailable) {
                // Navigates away; resolves in getRedirectResult after reload, so there's
                // no user to return here — callers rely on the auth state change.
                await signInWithRedirect(auth, googleProvider);
                return null;
            }
            throw err;
        }
    };

    const continueAsGuest = () => {
        setIsGuest(true);
        localStorage.setItem(GUEST_KEY, 'true');
    };

    const logout = async () => {
        await signOut(auth);
        setIsGuest(false);
        localStorage.removeItem(GUEST_KEY);
    };

    return (
        <AuthContext.Provider
            value={{ user, isGuest, loading, signInWithGoogle, continueAsGuest, logout }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
